import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import DynamicTable from './DynamicTable';

/**
 * FilterableTable - wrapper component that manages filter state and integrates with DynamicTable
 * 
 * This component solves the issue where applying filters causes the table to re-render and lose
 * internal state (like filter drawer state) by maintaining the filter state at a wrapper level.
 */
export const FilterableTable = ({
    // Data fetching props
    rows,
    isLoading = false,
    error = null,

    // DynamicTable props
    columns,
    getRowId,
    actionsRenderer,
    onSelectionChange,

    // Pagination props
    serverPagination = false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,

    // Filter props
    filterConfig = [],
    initialFilters = {},
    onFiltersChange, // called when filters change - parent can use this to trigger data fetch

    // Other DynamicTable props
    ...otherTableProps
}) => {
    // Manage filter state internally
    const [filters, setFilters] = useState(initialFilters);

    // Handle filter changes
    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);

        // Notify parent component so it can refetch data
        if (onFiltersChange) {
            onFiltersChange(newFilters);
        }
    };

    // Reset filters to initial state (useful for parent components)
    const resetFilters = () => {
        setFilters(initialFilters);
        if (onFiltersChange) {
            onFiltersChange(initialFilters);
        }
    };

    // Error display
    if (error) {
        return (
            <div style={{ padding: '16px', color: '#d32f2f', textAlign: 'center' }}>
                Error: {error.message || String(error)}
            </div>
        );
    }

    return (
        <DynamicTable
            {...otherTableProps}
            columns={columns}
            rows={rows}
            getRowId={getRowId}
            actionsRenderer={actionsRenderer}
            onSelectionChange={onSelectionChange}

            serverPagination={serverPagination}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}

            filterConfig={filterConfig}
            filters={filters}
            onFiltersChange={handleFiltersChange}

            isLoading={isLoading}
        />
    );
};

FilterableTable.propTypes = {
    // Data props
    rows: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.instanceOf(Error)]),

    // Table structure props
    columns: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        accessor: PropTypes.string.isRequired,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        align: PropTypes.oneOf(['left', 'right', 'center']),
        render: PropTypes.func,
        dynamicRender: PropTypes.func,
    })).isRequired,
    getRowId: PropTypes.func,
    actionsRenderer: PropTypes.func,
    onSelectionChange: PropTypes.func,

    // Pagination props
    serverPagination: PropTypes.bool,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    totalCount: PropTypes.number,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,

    // Filter props
    filterConfig: PropTypes.array,
    initialFilters: PropTypes.object,
    onFiltersChange: PropTypes.func,
};

export default FilterableTable;